import React, { useState } from 'react';
import { DocumentTextIcon, PhotoIcon, Square2StackIcon } from '@heroicons/react/24/outline';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragOverlay,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
  useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

// Helper function to convert number to ordinal (1st, 2nd, 3rd, etc.)
function getOrdinal(n) {
  const s = ["th", "st", "nd", "rd"];
  const v = n % 100;
  return n + (s[(v - 20) % 10] || s[v] || s[0]);
}

// Sortable layer item component
function SortableLayerItem({ element, index, isSelected, onSelectElement, getElementIcon, getElementLabel, getShapeColor, dragOverIndex, activeId, sortedElements }) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: element.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      onClick={() => onSelectElement(element.id)}
      {...attributes}
      {...listeners}
      className={`
        flex items-center gap-3 p-3 rounded-lg transition-all cursor-grab active:cursor-grabbing
        bg-white border shadow-sm
        ${isSelected ? 'bg-blue-50 border-blue-400 ring-2 ring-blue-200' : 'border-gray-200 hover:border-gray-300 hover:shadow-md'}
        ${isDragging ? 'opacity-50 shadow-xl border-blue-400 scale-105' : ''}
      `}
    >
      {/* Icon */}
      <div className="flex-shrink-0 flex items-center justify-center w-8 h-8">
        {element.type === 'image' && element.src ? (
          <div className="relative group">
            <img
              src={element.src}
              alt="Layer preview"
              className="h-8 w-8 object-cover rounded border border-gray-300 group-hover:scale-150 group-hover:shadow-lg transition-transform origin-left z-10"
            />
          </div>
        ) : element.type === 'shape' ? (
          element.shape === 'triangle' ? (
            <svg
              width="32"
              height="32"
              viewBox="0 0 20 20"
              fill={getShapeColor(element)}
              className="flex-shrink-0"
            >
              <polygon points="10,2 18,18 2,18" />
            </svg>
          ) : element.shape === 'circle' ? (
            <div
              className="h-8 w-8 rounded-full border border-gray-400"
              style={{ backgroundColor: getShapeColor(element) }}
            />
          ) : (
            <div
              className="h-8 w-8 border border-gray-400"
              style={{ backgroundColor: getShapeColor(element) }}
            />
          )
        ) : (
          <div className="h-8 w-8 flex items-center justify-center">
            {getElementIcon(element.type)}
          </div>
        )}
      </div>

      {/* Label */}
      <div className="flex-1 min-w-0">
        <p className="text-xs font-medium text-gray-700 truncate">
          {getElementLabel(element)}
        </p>
        <p className="text-xs text-gray-500">
          layer order: {getOrdinal(activeId && dragOverIndex !== null ? (() => {
            // Calculate new position during drag
            if (activeId === element.id) {
              // This is the dragged element
              return dragOverIndex + 1;
            } else if (activeId && sortedElements) {
              // Other elements - recalculate their positions
              const draggedElement = sortedElements.find(el => el.id === activeId);
              const draggedIndex = sortedElements.findIndex(el => el.id === activeId);
              
              if (draggedIndex < dragOverIndex) {
                // Dragged element moving down
                if (index > draggedIndex && index <= dragOverIndex) {
                  return index; // Shift up
                }
              } else if (draggedIndex > dragOverIndex) {
                // Dragged element moving up
                if (index >= dragOverIndex && index < draggedIndex) {
                  return index + 2; // Shift down
                }
              }
            }
            return index + 1;
          })() : index + 1)}
        </p>
      </div>
    </div>
  );
}

// Ghost element shown while dragging
function DraggedLayerGhost({ element, getElementIcon, getElementLabel, getShapeColor, dragIndex, totalElements }) {
  return (
    <div className="flex items-center gap-3 p-3 rounded-lg bg-white border-2 border-blue-400 shadow-xl">
      {/* Icon */}
      <div className="flex-shrink-0 flex items-center justify-center w-8 h-8">
        {element.type === 'image' && element.src ? (
          <img
            src={element.src}
            alt="Layer preview"
            className="h-8 w-8 object-cover rounded border border-gray-300"
          />
        ) : element.type === 'shape' ? (
          element.shape === 'triangle' ? (
            <svg
              width="32"
              height="32"
              viewBox="0 0 20 20"
              fill={getShapeColor(element)}
              className="flex-shrink-0"
            >
              <polygon points="10,2 18,18 2,18" />
            </svg>
          ) : element.shape === 'circle' ? (
            <div
              className="h-8 w-8 rounded-full border border-gray-400"
              style={{ backgroundColor: getShapeColor(element) }}
            />
          ) : (
            <div
              className="h-8 w-8 border border-gray-400"
              style={{ backgroundColor: getShapeColor(element) }}
            />
          )
        ) : (
          <div className="h-8 w-8 flex items-center justify-center">
            {getElementIcon(element.type)}
          </div>
        )}
      </div>

      {/* Label */}
      <div className="flex-1 min-w-0">
        <p className="text-xs font-medium text-gray-700 truncate">
          {getElementLabel(element)}
        </p>
        <p className="text-xs text-gray-500">
          layer order: {dragIndex !== undefined ? getOrdinal(dragIndex + 1) : getOrdinal(totalElements || 1)}
        </p>
      </div>
    </div>
  );
}

export default function LayersPanel({
  elements,
  selectedElement,
  onSelectElement,
  onReorderElements,
}) {
  const [activeId, setActiveId] = useState(null);
  const [dragOverIndex, setDragOverIndex] = useState(null);

  // Setup sensors for both mouse and touch drag detection
  const sensors = useSensors(
    useSensor(PointerSensor, {
      distance: 8, // 8px movement threshold before drag starts
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  if (!elements || elements.length === 0) {
    return (
      <div className="p-3">
        <p className="text-xs text-gray-500">No elements on this spread</p>
      </div>
    );
  }

  // Sort elements by z-index (highest first for visual representation)
  const sortedElements = [...elements].sort((a, b) => (b.zIndex || 0) - (a.zIndex || 0));
  const elementIds = sortedElements.map((el) => el.id);

  const getElementIcon = (type) => {
    switch (type) {
      case 'text':
        return <DocumentTextIcon className="h-4 w-4 text-blue-600 flex-shrink-0" />;
      case 'image':
        return <PhotoIcon className="h-4 w-4 text-green-600 flex-shrink-0" />;
      case 'shape':
        return <Square2StackIcon className="h-4 w-4 text-orange-600 flex-shrink-0" />;
      default:
        return null;
    }
  };

  const getElementLabel = (element) => {
    if (element.type === 'text') {
      const text = element.content || '';
      return text.substring(0, 30) + (text.length > 30 ? '...' : '');
    } else if (element.type === 'image') {
      return 'Image';
    } else if (element.type === 'shape') {
      return 'Shape';
    }
    return 'Element';
  };

  const getShapeColor = (element) => {
    if (element.type === 'shape' && element.style?.fill) {
      return element.style.fill;
    }
    return '#e5e7eb';
  };

  const handleDragStart = (event) => {
    setActiveId(event.active.id);
    onSelectElement(event.active.id);
  };

  const handleDragEnd = (event) => {
    const { active, over } = event;
    setActiveId(null);
    setDragOverIndex(null);

    if (active.id !== over?.id) {
      const oldIndex = elementIds.indexOf(active.id);
      const newIndex = elementIds.indexOf(over?.id);

      // Reorder using dnd-kit's arrayMove utility
      const reorderedIds = arrayMove(elementIds, oldIndex, newIndex);

      // Map reordered IDs back to elements with updated z-index
      const reorderedElements = reorderedIds.map((id, idx) => {
        const element = elements.find((el) => el.id === id);
        return {
          ...element,
          zIndex: reorderedIds.length - idx,
        };
      });

      // Keep original order in state, just update z-index values
      const finalElements = elements.map((el) => {
        const reordered = reorderedElements.find((re) => re.id === el.id);
        return reordered || el;
      });

      onReorderElements(finalElements);
    }
  };

  const handleDragOver = (event) => {
    const { over } = event;
    if (over) {
      const newIndex = elementIds.indexOf(over.id);
      setDragOverIndex(newIndex);
    }
  };

  const activeElement = sortedElements.find((el) => el.id === activeId);

  return (
    <div>
      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragStart={handleDragStart}
        onDragOver={handleDragOver}
        onDragEnd={handleDragEnd}
      >
        <SortableContext items={elementIds} strategy={verticalListSortingStrategy}>
          <div className="space-y-2 max-h-96 overflow-y-auto border border-gray-200 rounded-lg bg-gray-100 p-3">
            {sortedElements.map((element, index) => (
              <SortableLayerItem
                key={element.id}
                element={element}
                index={index}
                isSelected={selectedElement === element.id}
                onSelectElement={onSelectElement}
                getElementIcon={getElementIcon}
                getElementLabel={getElementLabel}
                getShapeColor={getShapeColor}
                dragOverIndex={dragOverIndex}
                activeId={activeId}
                sortedElements={sortedElements}
              />
            ))}
          </div>
        </SortableContext>

        {/* Ghost element that follows cursor while dragging */}
        <DragOverlay>
          {activeElement ? (
            <DraggedLayerGhost
              element={activeElement}
              getElementIcon={getElementIcon}
              getElementLabel={getElementLabel}
              getShapeColor={getShapeColor}
              dragIndex={dragOverIndex !== null ? dragOverIndex : elementIds.indexOf(activeId)}
              totalElements={sortedElements.length}
            />
          ) : null}
        </DragOverlay>
      </DndContext>

      <p className="text-xs text-gray-500 mt-2">
        💡 Click to select • Drag to reorder
      </p>
    </div>
  );
}
